import type { Language } from "../../types/i18n";

/*
 * UI strings for one area of the app, merged into `translations` (see
 * ./index.ts) so components read them through `useI18nStore().t` like every
 * other key. English is the reference; the other languages must carry the
 * same keys, which the record type enforces.
 *
 * Area: the Health Records page (/traveler/health) with its body data,
 * vital signs and wellbeing boxes (prefixes hrp, vital, wellb), and the
 * Emergency Profile page (hrpEp).
 */
const en = {
  // Health Records page
  hrpBannerDescription: "Manage your health data and medical information",
  hrpConfirmRequiredTitle: "Data confirmation required",
  hrpConfirmRequiredText:
    "Your health data will be deleted soon. Please confirm that your data is accurate.",
  hrpBasicData: "Basic Data",
  hrpAgeCalculated: "Age (calculated)",
  hrpNotAvailable: "Not available",
  hrpGenderDiverse: "Diverse",
  hrpBodyData: "Body Data",
  hrpWeightLastRecorded: "Weight, last recorded",
  hrpBmiLastRecorded: "BMI, last recorded",
  hrpBodyFatLastRecorded: "Body fat, last recorded",
  hrpNoDataRecorded: "No data recorded",
  hrpBmiUnderweight: "Underweight",
  hrpBmiNormal: "Normal weight",
  hrpBmiOverweight: "Overweight",
  hrpBmiObesity: "Obesity",
  hrpChartRangeLabel: "Chart time range",
  hrpRange7d: "7 days",
  hrpRange30d: "30 days",
  hrpRange90d: "90 days",
  hrpRange1y: "1 year",
  hrpRangeAll: "All",
  hrpWeightHistory: "Weight History ({count} entries)",
  hrpBodyFatHistory: "Body Fat History ({count} entries)",
  hrpNoWeightChartData: "No weight data available for the chart",
  hrpFirstEntriesAppear: "Your first entries will appear here",
  hrpCreateTestData: "Create FHIR Test Data",
  hrpCreateTestDataHint:
    "Creates FHIR R5 observations in the fHIRResource table",
  hrpSectionSaved: "{section} saved successfully.",
  hrpSectionSaveError: "Error saving {section}",
  hrpBodyDataSaved: "Body data saved successfully.",
  hrpInputErrorPrefix: "Input error",
  hrpErrorLoading: "Error loading health data",
  hrpErrorNoBodyValue:
    "Please enter a valid weight or body fat value before saving.",
  hrpErrorInvalidBodyInput: "Invalid input. Please check weight and height.",
  hrpErrorSavingBodyData: "Error saving body data",
  hrpErrorTestData: "Failed to create FHIR test data",
  hrpErrorSavingVitals: "Error saving vital signs",
  hrpErrorSavingLabResults: "Error saving lab results",
  hrpErrorLoadingLabResults: "Error loading lab results",
  hrpErrorUploadingLabReport: "Error uploading lab report",
  hrpLabResultsSaved: "Lab results saved successfully",
  hrpLabReportUploaded: "Lab report uploaded successfully",
  hrpIcdPlaceholder: "e.g. I21",
  hrpDisabilities: "Disabilities",
  hrpPhysicalDisability: "Physical Disability",
  hrpCognitiveDisability: "Cognitive Disability",
  hrpDisabilityType: "Type of disability",
  hrpTravelSupportNeeds: "Support needs when traveling",
  hrpSeverityMild: "Mild",
  hrpSeverityModerate: "Moderate",
  hrpSeveritySevere: "Severe",
  hrpSecurityTitle: "FHIR R5 & Encryption",
  hrpSecurityText:
    "All data is transmitted and stored encrypted using standardized health-data formats. Third parties get access only with your explicit consent.",
  hrpDataDeletionLabel: "Data deletion",
  hrpDataDeletionText:
    "After 12 months, unless you confirm it is still current.",
  hrpManageConsents: "Manage Consents",
  hrpConsentComingSoon: "Consent management is coming soon.",

  // Vital signs box
  vitalLoadError: "Error loading vital signs data",
  vitalSaveError: "Error saving vital signs data",

  // Wellbeing boxes
  wellbSaved: "Wellbeing saved successfully",
  wellbSaveFailed: "Failed to save wellbeing",

  // Emergency Profile page
  hrpEpLoadFailed: "Failed to load emergency data",
  hrpEpLoadFailedTitle: "Failed to Load Emergency Data",
  hrpEpRetry: "Retry",
  hrpEpGoToHealthRecords: "Go to Health Records",
  hrpEpQrGenerated: "QR code generated successfully",
  hrpEpQrGenerateFailed: "Failed to generate QR code",
  hrpEpQrRevoked: "QR code revoked successfully",
  hrpEpQrRevokeFailed: "Failed to revoke QR code",
  hrpEpConsentSaved: "Consent preferences saved successfully",
  hrpEpConsentSaveFailed: "Failed to save consent preferences",
  hrpEpNoData: "No emergency data available",
  hrpEpPdfDownloaded: "Emergency card PDF downloaded",
  hrpEpPdfFailed: "Failed to generate PDF",
  hrpEpToggleUnits: "Toggle unit system",
  hrpEpImperial: "Imperial",
  hrpEpMetric: "Metric",
  hrpEpViewQr: "View QR Code",
  hrpEpActiveUntil: "Active until {time}",
  hrpEpDownloadCard: "Download Emergency Card",
  hrpEpPrintablePdf: "Printable PDF (English/German)",
  hrpEpContactsComingSoon: "The emergency contacts feature is coming soon.",
  hrpEpPriorityTabs: "Priority tabs",
  hrpEpP1Intro:
    "This data is critical for emergency responders. Keep it up to date.",
  hrpEpP2Intro: "Important medical information that helps guide treatment.",
  hrpEpP3Intro: "Helpful additional medical history and observations.",
  hrpEpAllergies: "Allergies",
  hrpEpConditions: "Conditions",
  hrpEpEditInHealthRecords: "Edit in Health Records",
  hrpEpNoAllergies: "No allergies recorded",
  hrpEpNoMedications: "No medications recorded",
  hrpEpNoConditions: "No conditions recorded",
  hrpEpNoContacts: "No emergency contacts recorded",
  hrpEpNotRecordedYet: "Not recorded yet",
  hrpEpNoImmunizations: "No immunizations recorded",
  hrpEpNoProcedures: "No procedures recorded",
  hrpEpClinicalObservations: "Clinical Observations",
  hrpEpNoObservations: "No observations recorded",
  hrpEpTravelHistory: "Travel History",
  hrpEpNoTravelHistory: "No travel history recorded",
  hrpEpCriticalityHigh: "high risk",
  hrpEpCriticalityLow: "low risk",
  hrpEpCriticalityUnknown: "risk not assessed",
  hrpEpStatusActive: "active",
  hrpEpStatusInactive: "inactive",
  hrpEpStatusResolved: "resolved",
  hrpEpStatusRemission: "in remission",
  hrpEpStatusRecurrence: "recurrence",
  hrpEpConsentTitle: "Emergency Data Sharing Consent",
  hrpEpConsentIntro:
    "Allow emergency responders to access your critical health data via QR code or an emergency access token.",
  hrpEpShareP1: "Share Priority 1 Data (Critical)",
  hrpEpShareP1Items: "Allergies, Medications, Conditions, Emergency Contacts",
  hrpEpShareP2: "Share Priority 2 Data (Important)",
  hrpEpShareP2Items:
    "Blood Type, Medical Devices, Immunizations, Height/Weight",
  hrpEpShareP3: "Share Priority 3 Data (Helpful)",
  hrpEpShareP3Items:
    "Medical Procedures, Clinical Observations, Travel History",
  hrpEpConsentAgree: "I understand and consent to emergency data sharing",
  hrpEpConsentAgreeShort: "I understand and consent",
  hrpEpConsentAgreeText:
    "I understand that emergency responders with a valid access token can view the health data I have selected. I can revoke this consent at any time. Access is logged and time-limited.",
  hrpEpSaveConsent: "Save Consent Preferences",
  hrpEpKeepUpToDate:
    "Keep your emergency profile up to date. This data is shared with emergency responders when you generate a QR code.",
  hrpEpProfileValidation: "Profile Validation",
  hrpEpAccessAuditLog: "Access Audit Log",
  hrpEpConsentActive: "Emergency Sharing Consent Active",
  hrpEpConsentGivenOn: "Consent given on {date}",
  hrpEpDemographics: "Demographics",
  hrpEpDemographicsDetail: "Demographics (Blood Type, Height, Weight)",
  hrpEpMedicalDevices: "Medical Devices",
  hrpEpConsentDialogIntro:
    "Select which data you want to share with emergency responders. Your consent is saved for future QR codes.",
  hrpEpP1Heading: "Priority 1: Critical Data",
  hrpEpP2Heading: "Priority 2: Important Data",
  hrpEpConsentNote:
    "We recommend sharing all critical data to ensure proper emergency care. You can revoke access at any time.",
  hrpEpConfirmGenerate: "Confirm & Generate QR Code",
  hrpEpExpires: "Expires",
  hrpEpExpiryNote:
    "For security, this QR code expires automatically after the set duration.",
  hrpEpLinkCopied: "Link copied to clipboard",
  hrpEpRevokeAccess: "Revoke Access",
};

export type HealthPageStrings = typeof en;

const de: HealthPageStrings = {
  hrpBannerDescription:
    "Verwalten Sie Ihre Gesundheitsdaten und medizinischen Informationen",
  hrpConfirmRequiredTitle: "Bestätigung der Daten erforderlich",
  hrpConfirmRequiredText:
    "Ihre Gesundheitsdaten werden bald gelöscht. Bitte bestätigen Sie, dass Ihre Daten aktuell sind.",
  hrpBasicData: "Basisdaten",
  hrpAgeCalculated: "Alter (berechnet)",
  hrpNotAvailable: "Nicht verfügbar",
  hrpGenderDiverse: "Divers",
  hrpBodyData: "Körperdaten",
  hrpWeightLastRecorded: "Gewicht, zuletzt erfasst",
  hrpBmiLastRecorded: "BMI, zuletzt erfasst",
  hrpBodyFatLastRecorded: "Körperfett, zuletzt erfasst",
  hrpNoDataRecorded: "Keine Daten erfasst",
  hrpBmiUnderweight: "Untergewicht",
  hrpBmiNormal: "Normalgewicht",
  hrpBmiOverweight: "Übergewicht",
  hrpBmiObesity: "Adipositas",
  hrpChartRangeLabel: "Zeitraum des Diagramms",
  hrpRange7d: "7 Tage",
  hrpRange30d: "30 Tage",
  hrpRange90d: "90 Tage",
  hrpRange1y: "1 Jahr",
  hrpRangeAll: "Alle",
  hrpWeightHistory: "Gewichtsverlauf ({count} Einträge)",
  hrpBodyFatHistory: "Körperfettverlauf ({count} Einträge)",
  hrpNoWeightChartData: "Keine Gewichtsdaten für das Diagramm verfügbar",
  hrpFirstEntriesAppear: "Ihre ersten Einträge erscheinen hier",
  hrpCreateTestData: "FHIR-Testdaten erstellen",
  hrpCreateTestDataHint:
    "Erstellt FHIR-R5-Observations in der Tabelle fHIRResource",
  hrpSectionSaved: "{section} erfolgreich gespeichert.",
  hrpSectionSaveError: "Fehler beim Speichern: {section}",
  hrpBodyDataSaved: "Körperdaten erfolgreich gespeichert.",
  hrpInputErrorPrefix: "Eingabefehler",
  hrpErrorLoading: "Fehler beim Laden der Gesundheitsdaten",
  hrpErrorNoBodyValue:
    "Bitte geben Sie vor dem Speichern ein gültiges Gewicht oder einen gültigen Körperfettwert ein.",
  hrpErrorInvalidBodyInput:
    "Ungültige Eingabe. Bitte prüfen Sie Gewicht und Größe.",
  hrpErrorSavingBodyData: "Fehler beim Speichern der Körperdaten",
  hrpErrorTestData: "FHIR-Testdaten konnten nicht erstellt werden",
  hrpErrorSavingVitals: "Fehler beim Speichern der Vitalwerte",
  hrpErrorSavingLabResults: "Fehler beim Speichern der Laborwerte",
  hrpErrorLoadingLabResults: "Fehler beim Laden der Laborwerte",
  hrpErrorUploadingLabReport: "Fehler beim Hochladen des Laborbefunds",
  hrpLabResultsSaved: "Laborwerte erfolgreich gespeichert",
  hrpLabReportUploaded: "Laborbefund erfolgreich hochgeladen",
  hrpIcdPlaceholder: "z. B. I21",
  hrpDisabilities: "Beeinträchtigungen",
  hrpPhysicalDisability: "Körperliche Beeinträchtigung",
  hrpCognitiveDisability: "Kognitive Beeinträchtigung",
  hrpDisabilityType: "Art der Beeinträchtigung",
  hrpTravelSupportNeeds: "Unterstützungsbedarf auf Reisen",
  hrpSeverityMild: "Leicht",
  hrpSeverityModerate: "Mittel",
  hrpSeveritySevere: "Schwer",
  hrpSecurityTitle: "FHIR R5 & Verschlüsselung",
  hrpSecurityText:
    "Alle Daten werden verschlüsselt und in standardisierten Gesundheitsdatenformaten übertragen und gespeichert. Dritte erhalten nur mit Ihrer ausdrücklichen Einwilligung Zugriff.",
  hrpDataDeletionLabel: "Datenlöschung",
  hrpDataDeletionText:
    "Nach 12 Monaten, sofern Sie die Aktualität nicht bestätigen.",
  hrpManageConsents: "Einwilligungen verwalten",
  hrpConsentComingSoon: "Die Einwilligungsverwaltung folgt in Kürze.",

  vitalLoadError: "Fehler beim Laden der Vitalwerte",
  vitalSaveError: "Fehler beim Speichern der Vitalwerte",

  wellbSaved: "Wohlbefinden erfolgreich gespeichert",
  wellbSaveFailed: "Wohlbefinden konnte nicht gespeichert werden",

  hrpEpLoadFailed: "Notfalldaten konnten nicht geladen werden",
  hrpEpLoadFailedTitle: "Notfalldaten konnten nicht geladen werden",
  hrpEpRetry: "Erneut versuchen",
  hrpEpGoToHealthRecords: "Zu den Gesundheitsdaten",
  hrpEpQrGenerated: "QR-Code erfolgreich erstellt",
  hrpEpQrGenerateFailed: "QR-Code konnte nicht erstellt werden",
  hrpEpQrRevoked: "QR-Code erfolgreich widerrufen",
  hrpEpQrRevokeFailed: "QR-Code konnte nicht widerrufen werden",
  hrpEpConsentSaved: "Einwilligungseinstellungen erfolgreich gespeichert",
  hrpEpConsentSaveFailed:
    "Einwilligungseinstellungen konnten nicht gespeichert werden",
  hrpEpNoData: "Keine Notfalldaten verfügbar",
  hrpEpPdfDownloaded: "Notfallausweis als PDF heruntergeladen",
  hrpEpPdfFailed: "PDF konnte nicht erstellt werden",
  hrpEpToggleUnits: "Einheitensystem wechseln",
  hrpEpImperial: "US-Einheiten",
  hrpEpMetric: "Metrisch",
  hrpEpViewQr: "QR-Code anzeigen",
  hrpEpActiveUntil: "Aktiv bis {time}",
  hrpEpDownloadCard: "Notfallausweis herunterladen",
  hrpEpPrintablePdf: "Druckbares PDF (Deutsch/Englisch)",
  hrpEpContactsComingSoon: "Die Notfallkontakte-Funktion folgt in Kürze.",
  hrpEpPriorityTabs: "Prioritäts-Reiter",
  hrpEpP1Intro:
    "Diese Daten sind für Rettungskräfte entscheidend. Halten Sie sie aktuell.",
  hrpEpP2Intro:
    "Wichtige medizinische Informationen, die Behandlungsentscheidungen unterstützen.",
  hrpEpP3Intro: "Hilfreiche ergänzende Krankengeschichte und Befunde.",
  hrpEpAllergies: "Allergien",
  hrpEpConditions: "Erkrankungen",
  hrpEpEditInHealthRecords: "In den Gesundheitsdaten bearbeiten",
  hrpEpNoAllergies: "Keine Allergien erfasst",
  hrpEpNoMedications: "Keine Medikamente erfasst",
  hrpEpNoConditions: "Keine Erkrankungen erfasst",
  hrpEpNoContacts: "Keine Notfallkontakte erfasst",
  hrpEpNotRecordedYet: "Noch nicht erfasst",
  hrpEpNoImmunizations: "Keine Impfungen erfasst",
  hrpEpNoProcedures: "Keine Eingriffe erfasst",
  hrpEpClinicalObservations: "Klinische Befunde",
  hrpEpNoObservations: "Keine Befunde erfasst",
  hrpEpTravelHistory: "Reiseverlauf",
  hrpEpNoTravelHistory: "Kein Reiseverlauf erfasst",
  hrpEpCriticalityHigh: "hohes Risiko",
  hrpEpCriticalityLow: "geringes Risiko",
  hrpEpCriticalityUnknown: "Risiko nicht bewertet",
  hrpEpStatusActive: "aktiv",
  hrpEpStatusInactive: "inaktiv",
  hrpEpStatusResolved: "abgeheilt",
  hrpEpStatusRemission: "in Remission",
  hrpEpStatusRecurrence: "Rezidiv",
  hrpEpConsentTitle: "Einwilligung zur Freigabe von Notfalldaten",
  hrpEpConsentIntro:
    "Erlauben Sie Rettungskräften, per QR-Code oder Notfallzugangstoken auf Ihre kritischen Gesundheitsdaten zuzugreifen.",
  hrpEpShareP1: "Priorität-1-Daten freigeben (kritisch)",
  hrpEpShareP1Items: "Allergien, Medikamente, Erkrankungen, Notfallkontakte",
  hrpEpShareP2: "Priorität-2-Daten freigeben (wichtig)",
  hrpEpShareP2Items: "Blutgruppe, Medizinprodukte, Impfungen, Größe/Gewicht",
  hrpEpShareP3: "Priorität-3-Daten freigeben (hilfreich)",
  hrpEpShareP3Items: "Medizinische Eingriffe, klinische Befunde, Reiseverlauf",
  hrpEpConsentAgree:
    "Ich habe verstanden und willige in die Freigabe der Notfalldaten ein",
  hrpEpConsentAgreeShort: "Ich habe verstanden und willige ein",
  hrpEpConsentAgreeText:
    "Mir ist bewusst, dass Rettungskräfte mit einem gültigen Zugangstoken die von mir ausgewählten Gesundheitsdaten einsehen können. Ich kann diese Einwilligung jederzeit widerrufen. Zugriffe werden protokolliert und sind zeitlich begrenzt.",
  hrpEpSaveConsent: "Einwilligung speichern",
  hrpEpKeepUpToDate:
    "Halten Sie Ihr Notfallprofil aktuell. Diese Daten werden mit Rettungskräften geteilt, wenn Sie einen QR-Code erstellen.",
  hrpEpProfileValidation: "Profilprüfung",
  hrpEpAccessAuditLog: "Zugriffsprotokoll",
  hrpEpConsentActive: "Einwilligung zur Notfallfreigabe aktiv",
  hrpEpConsentGivenOn: "Einwilligung erteilt am {date}",
  hrpEpDemographics: "Basisdaten",
  hrpEpDemographicsDetail: "Basisdaten (Blutgruppe, Größe, Gewicht)",
  hrpEpMedicalDevices: "Medizinprodukte",
  hrpEpConsentDialogIntro:
    "Wählen Sie aus, welche Daten Sie mit Rettungskräften teilen möchten. Ihre Einwilligung wird für künftige QR-Codes gespeichert.",
  hrpEpP1Heading: "Priorität 1: Kritische Daten",
  hrpEpP2Heading: "Priorität 2: Wichtige Daten",
  hrpEpConsentNote:
    "Wir empfehlen, alle kritischen Daten freizugeben, damit eine angemessene Notfallversorgung möglich ist. Sie können den Zugriff jederzeit widerrufen.",
  hrpEpConfirmGenerate: "Bestätigen & QR-Code erstellen",
  hrpEpExpires: "Läuft ab",
  hrpEpExpiryNote:
    "Aus Sicherheitsgründen läuft dieser QR-Code nach der festgelegten Dauer automatisch ab.",
  hrpEpLinkCopied: "Link in die Zwischenablage kopiert",
  hrpEpRevokeAccess: "Zugriff widerrufen",
};

const fr: HealthPageStrings = {
  hrpBannerDescription:
    "Gérez vos données de santé et vos informations médicales",
  hrpConfirmRequiredTitle: "Confirmation des données requise",
  hrpConfirmRequiredText:
    "Vos données de santé seront bientôt supprimées. Veuillez confirmer qu'elles sont exactes.",
  hrpBasicData: "Données de base",
  hrpAgeCalculated: "Âge (calculé)",
  hrpNotAvailable: "Non disponible",
  hrpGenderDiverse: "Divers",
  hrpBodyData: "Données corporelles",
  hrpWeightLastRecorded: "Poids, dernière mesure",
  hrpBmiLastRecorded: "IMC, dernière mesure",
  hrpBodyFatLastRecorded: "Masse grasse, dernière mesure",
  hrpNoDataRecorded: "Aucune donnée enregistrée",
  hrpBmiUnderweight: "Insuffisance pondérale",
  hrpBmiNormal: "Poids normal",
  hrpBmiOverweight: "Surpoids",
  hrpBmiObesity: "Obésité",
  hrpChartRangeLabel: "Période du graphique",
  hrpRange7d: "7 jours",
  hrpRange30d: "30 jours",
  hrpRange90d: "90 jours",
  hrpRange1y: "1 an",
  hrpRangeAll: "Tout",
  hrpWeightHistory: "Évolution du poids ({count} entrées)",
  hrpBodyFatHistory: "Évolution de la masse grasse ({count} entrées)",
  hrpNoWeightChartData: "Aucune donnée de poids disponible pour le graphique",
  hrpFirstEntriesAppear: "Vos premières entrées apparaîtront ici",
  hrpCreateTestData: "Créer des données de test FHIR",
  hrpCreateTestDataHint:
    "Crée des observations FHIR R5 dans la table fHIRResource",
  hrpSectionSaved: "{section} enregistré avec succès.",
  hrpSectionSaveError: "Erreur lors de l'enregistrement : {section}",
  hrpBodyDataSaved: "Données corporelles enregistrées avec succès.",
  hrpInputErrorPrefix: "Erreur de saisie",
  hrpErrorLoading: "Erreur lors du chargement des données de santé",
  hrpErrorNoBodyValue:
    "Veuillez saisir un poids ou un taux de masse grasse valide avant d'enregistrer.",
  hrpErrorInvalidBodyInput:
    "Saisie invalide. Veuillez vérifier le poids et la taille.",
  hrpErrorSavingBodyData:
    "Erreur lors de l'enregistrement des données corporelles",
  hrpErrorTestData: "Impossible de créer les données de test FHIR",
  hrpErrorSavingVitals: "Erreur lors de l'enregistrement des signes vitaux",
  hrpErrorSavingLabResults:
    "Erreur lors de l'enregistrement des résultats d'analyses",
  hrpErrorLoadingLabResults:
    "Erreur lors du chargement des résultats d'analyses",
  hrpErrorUploadingLabReport:
    "Erreur lors de l'envoi du compte rendu d'analyses",
  hrpLabResultsSaved: "Résultats d'analyses enregistrés avec succès",
  hrpLabReportUploaded: "Compte rendu d'analyses envoyé avec succès",
  hrpIcdPlaceholder: "p. ex. I21",
  hrpDisabilities: "Handicaps",
  hrpPhysicalDisability: "Handicap physique",
  hrpCognitiveDisability: "Handicap cognitif",
  hrpDisabilityType: "Type de handicap",
  hrpTravelSupportNeeds: "Besoins d'assistance en voyage",
  hrpSeverityMild: "Léger",
  hrpSeverityModerate: "Modéré",
  hrpSeveritySevere: "Sévère",
  hrpSecurityTitle: "FHIR R5 et chiffrement",
  hrpSecurityText:
    "Toutes les données sont transmises et stockées de façon chiffrée, dans des formats de données de santé standardisés. Les tiers n'y accèdent qu'avec votre consentement explicite.",
  hrpDataDeletionLabel: "Suppression des données",
  hrpDataDeletionText:
    "Après 12 mois, sauf si vous confirmez qu'elles sont à jour.",
  hrpManageConsents: "Gérer les consentements",
  hrpConsentComingSoon: "La gestion des consentements arrive bientôt.",

  vitalLoadError: "Erreur lors du chargement des signes vitaux",
  vitalSaveError: "Erreur lors de l'enregistrement des signes vitaux",

  wellbSaved: "Bien-être enregistré avec succès",
  wellbSaveFailed: "Impossible d'enregistrer le bien-être",

  hrpEpLoadFailed: "Impossible de charger les données d'urgence",
  hrpEpLoadFailedTitle: "Impossible de charger les données d'urgence",
  hrpEpRetry: "Réessayer",
  hrpEpGoToHealthRecords: "Aller aux données de santé",
  hrpEpQrGenerated: "Code QR généré avec succès",
  hrpEpQrGenerateFailed: "Impossible de générer le code QR",
  hrpEpQrRevoked: "Code QR révoqué avec succès",
  hrpEpQrRevokeFailed: "Impossible de révoquer le code QR",
  hrpEpConsentSaved: "Préférences de consentement enregistrées avec succès",
  hrpEpConsentSaveFailed:
    "Impossible d'enregistrer les préférences de consentement",
  hrpEpNoData: "Aucune donnée d'urgence disponible",
  hrpEpPdfDownloaded: "Carte d'urgence PDF téléchargée",
  hrpEpPdfFailed: "Impossible de générer le PDF",
  hrpEpToggleUnits: "Changer de système d'unités",
  hrpEpImperial: "Impérial",
  hrpEpMetric: "Métrique",
  hrpEpViewQr: "Afficher le code QR",
  hrpEpActiveUntil: "Actif jusqu'à {time}",
  hrpEpDownloadCard: "Télécharger la carte d'urgence",
  hrpEpPrintablePdf: "PDF imprimable (anglais/allemand)",
  hrpEpContactsComingSoon: "La fonction des contacts d'urgence arrive bientôt.",
  hrpEpPriorityTabs: "Onglets de priorité",
  hrpEpP1Intro:
    "Ces données sont essentielles pour les secours. Tenez-les à jour.",
  hrpEpP2Intro:
    "Informations médicales importantes qui aident aux décisions de traitement.",
  hrpEpP3Intro: "Antécédents médicaux et observations complémentaires utiles.",
  hrpEpAllergies: "Allergies",
  hrpEpConditions: "Pathologies",
  hrpEpEditInHealthRecords: "Modifier dans les données de santé",
  hrpEpNoAllergies: "Aucune allergie enregistrée",
  hrpEpNoMedications: "Aucun médicament enregistré",
  hrpEpNoConditions: "Aucune pathologie enregistrée",
  hrpEpNoContacts: "Aucun contact d'urgence enregistré",
  hrpEpNotRecordedYet: "Pas encore enregistré",
  hrpEpNoImmunizations: "Aucune vaccination enregistrée",
  hrpEpNoProcedures: "Aucune intervention enregistrée",
  hrpEpClinicalObservations: "Observations cliniques",
  hrpEpNoObservations: "Aucune observation enregistrée",
  hrpEpTravelHistory: "Historique des voyages",
  hrpEpNoTravelHistory: "Aucun historique de voyage enregistré",
  hrpEpCriticalityHigh: "risque élevé",
  hrpEpCriticalityLow: "risque faible",
  hrpEpCriticalityUnknown: "risque non évalué",
  hrpEpStatusActive: "active",
  hrpEpStatusInactive: "inactive",
  hrpEpStatusResolved: "guérie",
  hrpEpStatusRemission: "en rémission",
  hrpEpStatusRecurrence: "récidive",
  hrpEpConsentTitle: "Consentement au partage des données d'urgence",
  hrpEpConsentIntro:
    "Autorisez les secours à accéder à vos données de santé critiques via un code QR ou un jeton d'accès d'urgence.",
  hrpEpShareP1: "Partager les données de priorité 1 (critiques)",
  hrpEpShareP1Items: "Allergies, médicaments, pathologies, contacts d'urgence",
  hrpEpShareP2: "Partager les données de priorité 2 (importantes)",
  hrpEpShareP2Items:
    "Groupe sanguin, dispositifs médicaux, vaccinations, taille/poids",
  hrpEpShareP3: "Partager les données de priorité 3 (utiles)",
  hrpEpShareP3Items:
    "Interventions médicales, observations cliniques, historique des voyages",
  hrpEpConsentAgree:
    "J'ai compris et je consens au partage des données d'urgence",
  hrpEpConsentAgreeShort: "J'ai compris et je consens",
  hrpEpConsentAgreeText:
    "Je comprends que les secours disposant d'un jeton d'accès valide peuvent consulter les données de santé que j'ai sélectionnées. Je peux révoquer ce consentement à tout moment. Les accès sont journalisés et limités dans le temps.",
  hrpEpSaveConsent: "Enregistrer le consentement",
  hrpEpKeepUpToDate:
    "Tenez votre profil d'urgence à jour. Ces données sont partagées avec les secours lorsque vous générez un code QR.",
  hrpEpProfileValidation: "Validation du profil",
  hrpEpAccessAuditLog: "Journal des accès",
  hrpEpConsentActive: "Consentement au partage d'urgence actif",
  hrpEpConsentGivenOn: "Consentement donné le {date}",
  hrpEpDemographics: "Données personnelles",
  hrpEpDemographicsDetail:
    "Données personnelles (groupe sanguin, taille, poids)",
  hrpEpMedicalDevices: "Dispositifs médicaux",
  hrpEpConsentDialogIntro:
    "Choisissez les données à partager avec les secours. Votre consentement est enregistré pour les prochains codes QR.",
  hrpEpP1Heading: "Priorité 1 : données critiques",
  hrpEpP2Heading: "Priorité 2 : données importantes",
  hrpEpConsentNote:
    "Nous recommandons de partager toutes les données critiques pour garantir une prise en charge adaptée. Vous pouvez révoquer l'accès à tout moment.",
  hrpEpConfirmGenerate: "Confirmer et générer le code QR",
  hrpEpExpires: "Expire",
  hrpEpExpiryNote:
    "Pour des raisons de sécurité, ce code QR expire automatiquement après la durée définie.",
  hrpEpLinkCopied: "Lien copié dans le presse-papiers",
  hrpEpRevokeAccess: "Révoquer l'accès",
};

const es: HealthPageStrings = {
  hrpBannerDescription: "Gestione sus datos de salud e información médica",
  hrpConfirmRequiredTitle: "Se requiere confirmar los datos",
  hrpConfirmRequiredText:
    "Sus datos de salud se eliminarán pronto. Confirme que sus datos son correctos.",
  hrpBasicData: "Datos básicos",
  hrpAgeCalculated: "Edad (calculada)",
  hrpNotAvailable: "No disponible",
  hrpGenderDiverse: "Diverso",
  hrpBodyData: "Datos corporales",
  hrpWeightLastRecorded: "Peso, último registro",
  hrpBmiLastRecorded: "IMC, último registro",
  hrpBodyFatLastRecorded: "Grasa corporal, último registro",
  hrpNoDataRecorded: "No hay datos registrados",
  hrpBmiUnderweight: "Bajo peso",
  hrpBmiNormal: "Peso normal",
  hrpBmiOverweight: "Sobrepeso",
  hrpBmiObesity: "Obesidad",
  hrpChartRangeLabel: "Periodo del gráfico",
  hrpRange7d: "7 días",
  hrpRange30d: "30 días",
  hrpRange90d: "90 días",
  hrpRange1y: "1 año",
  hrpRangeAll: "Todo",
  hrpWeightHistory: "Evolución del peso ({count} registros)",
  hrpBodyFatHistory: "Evolución de la grasa corporal ({count} registros)",
  hrpNoWeightChartData: "No hay datos de peso para el gráfico",
  hrpFirstEntriesAppear: "Sus primeros registros aparecerán aquí",
  hrpCreateTestData: "Crear datos de prueba FHIR",
  hrpCreateTestDataHint: "Crea observaciones FHIR R5 en la tabla fHIRResource",
  hrpSectionSaved: "{section} guardado correctamente.",
  hrpSectionSaveError: "Error al guardar: {section}",
  hrpBodyDataSaved: "Datos corporales guardados correctamente.",
  hrpInputErrorPrefix: "Error de entrada",
  hrpErrorLoading: "Error al cargar los datos de salud",
  hrpErrorNoBodyValue:
    "Introduzca un peso o un porcentaje de grasa corporal válido antes de guardar.",
  hrpErrorInvalidBodyInput:
    "Datos no válidos. Compruebe el peso y la estatura.",
  hrpErrorSavingBodyData: "Error al guardar los datos corporales",
  hrpErrorTestData: "No se pudieron crear los datos de prueba FHIR",
  hrpErrorSavingVitals: "Error al guardar las constantes vitales",
  hrpErrorSavingLabResults: "Error al guardar los resultados de laboratorio",
  hrpErrorLoadingLabResults: "Error al cargar los resultados de laboratorio",
  hrpErrorUploadingLabReport: "Error al subir el informe de laboratorio",
  hrpLabResultsSaved: "Resultados de laboratorio guardados correctamente",
  hrpLabReportUploaded: "Informe de laboratorio subido correctamente",
  hrpIcdPlaceholder: "p. ej., I21",
  hrpDisabilities: "Discapacidades",
  hrpPhysicalDisability: "Discapacidad física",
  hrpCognitiveDisability: "Discapacidad cognitiva",
  hrpDisabilityType: "Tipo de discapacidad",
  hrpTravelSupportNeeds: "Necesidades de asistencia al viajar",
  hrpSeverityMild: "Leve",
  hrpSeverityModerate: "Moderada",
  hrpSeveritySevere: "Grave",
  hrpSecurityTitle: "FHIR R5 y cifrado",
  hrpSecurityText:
    "Todos los datos se transmiten y almacenan cifrados en formatos estandarizados de datos de salud. Terceros solo acceden con su consentimiento expreso.",
  hrpDataDeletionLabel: "Eliminación de datos",
  hrpDataDeletionText: "Tras 12 meses, salvo que confirme que siguen vigentes.",
  hrpManageConsents: "Gestionar consentimientos",
  hrpConsentComingSoon: "La gestión de consentimientos llegará pronto.",

  vitalLoadError: "Error al cargar las constantes vitales",
  vitalSaveError: "Error al guardar las constantes vitales",

  wellbSaved: "Bienestar guardado correctamente",
  wellbSaveFailed: "No se pudo guardar el bienestar",

  hrpEpLoadFailed: "No se pudieron cargar los datos de emergencia",
  hrpEpLoadFailedTitle: "No se pudieron cargar los datos de emergencia",
  hrpEpRetry: "Reintentar",
  hrpEpGoToHealthRecords: "Ir a los datos de salud",
  hrpEpQrGenerated: "Código QR generado correctamente",
  hrpEpQrGenerateFailed: "No se pudo generar el código QR",
  hrpEpQrRevoked: "Código QR revocado correctamente",
  hrpEpQrRevokeFailed: "No se pudo revocar el código QR",
  hrpEpConsentSaved: "Preferencias de consentimiento guardadas correctamente",
  hrpEpConsentSaveFailed:
    "No se pudieron guardar las preferencias de consentimiento",
  hrpEpNoData: "No hay datos de emergencia disponibles",
  hrpEpPdfDownloaded: "PDF de la tarjeta de emergencia descargado",
  hrpEpPdfFailed: "No se pudo generar el PDF",
  hrpEpToggleUnits: "Cambiar sistema de unidades",
  hrpEpImperial: "Imperial",
  hrpEpMetric: "Métrico",
  hrpEpViewQr: "Ver código QR",
  hrpEpActiveUntil: "Activo hasta las {time}",
  hrpEpDownloadCard: "Descargar tarjeta de emergencia",
  hrpEpPrintablePdf: "PDF imprimible (inglés/alemán)",
  hrpEpContactsComingSoon:
    "La función de contactos de emergencia llegará pronto.",
  hrpEpPriorityTabs: "Pestañas de prioridad",
  hrpEpP1Intro:
    "Estos datos son esenciales para los servicios de emergencia. Manténgalos actualizados.",
  hrpEpP2Intro:
    "Información médica importante que ayuda en las decisiones de tratamiento.",
  hrpEpP3Intro: "Historial médico y observaciones adicionales útiles.",
  hrpEpAllergies: "Alergias",
  hrpEpConditions: "Enfermedades",
  hrpEpEditInHealthRecords: "Editar en los datos de salud",
  hrpEpNoAllergies: "No hay alergias registradas",
  hrpEpNoMedications: "No hay medicamentos registrados",
  hrpEpNoConditions: "No hay enfermedades registradas",
  hrpEpNoContacts: "No hay contactos de emergencia registrados",
  hrpEpNotRecordedYet: "Aún no registrado",
  hrpEpNoImmunizations: "No hay vacunas registradas",
  hrpEpNoProcedures: "No hay intervenciones registradas",
  hrpEpClinicalObservations: "Observaciones clínicas",
  hrpEpNoObservations: "No hay observaciones registradas",
  hrpEpTravelHistory: "Historial de viajes",
  hrpEpNoTravelHistory: "No hay historial de viajes registrado",
  hrpEpCriticalityHigh: "riesgo alto",
  hrpEpCriticalityLow: "riesgo bajo",
  hrpEpCriticalityUnknown: "riesgo no evaluado",
  hrpEpStatusActive: "activa",
  hrpEpStatusInactive: "inactiva",
  hrpEpStatusResolved: "resuelta",
  hrpEpStatusRemission: "en remisión",
  hrpEpStatusRecurrence: "recidiva",
  hrpEpConsentTitle: "Consentimiento para compartir datos de emergencia",
  hrpEpConsentIntro:
    "Permita que los servicios de emergencia accedan a sus datos de salud críticos mediante un código QR o un token de acceso de emergencia.",
  hrpEpShareP1: "Compartir datos de prioridad 1 (críticos)",
  hrpEpShareP1Items:
    "Alergias, medicamentos, enfermedades, contactos de emergencia",
  hrpEpShareP2: "Compartir datos de prioridad 2 (importantes)",
  hrpEpShareP2Items:
    "Grupo sanguíneo, dispositivos médicos, vacunas, estatura/peso",
  hrpEpShareP3: "Compartir datos de prioridad 3 (útiles)",
  hrpEpShareP3Items:
    "Intervenciones médicas, observaciones clínicas, historial de viajes",
  hrpEpConsentAgree:
    "Entiendo y doy mi consentimiento para compartir los datos de emergencia",
  hrpEpConsentAgreeShort: "Entiendo y doy mi consentimiento",
  hrpEpConsentAgreeText:
    "Entiendo que los servicios de emergencia con un token de acceso válido pueden ver los datos de salud que he seleccionado. Puedo revocar este consentimiento en cualquier momento. Los accesos se registran y tienen un tiempo limitado.",
  hrpEpSaveConsent: "Guardar consentimiento",
  hrpEpKeepUpToDate:
    "Mantenga su perfil de emergencia actualizado. Estos datos se comparten con los servicios de emergencia cuando genera un código QR.",
  hrpEpProfileValidation: "Validación del perfil",
  hrpEpAccessAuditLog: "Registro de accesos",
  hrpEpConsentActive: "Consentimiento de emergencia activo",
  hrpEpConsentGivenOn: "Consentimiento otorgado el {date}",
  hrpEpDemographics: "Datos personales",
  hrpEpDemographicsDetail: "Datos personales (grupo sanguíneo, estatura, peso)",
  hrpEpMedicalDevices: "Dispositivos médicos",
  hrpEpConsentDialogIntro:
    "Seleccione qué datos desea compartir con los servicios de emergencia. Su consentimiento se guarda para futuros códigos QR.",
  hrpEpP1Heading: "Prioridad 1: datos críticos",
  hrpEpP2Heading: "Prioridad 2: datos importantes",
  hrpEpConsentNote:
    "Recomendamos compartir todos los datos críticos para garantizar una atención de emergencia adecuada. Puede revocar el acceso en cualquier momento.",
  hrpEpConfirmGenerate: "Confirmar y generar código QR",
  hrpEpExpires: "Caduca",
  hrpEpExpiryNote:
    "Por seguridad, este código QR caduca automáticamente tras el periodo establecido.",
  hrpEpLinkCopied: "Enlace copiado al portapapeles",
  hrpEpRevokeAccess: "Revocar acceso",
};

const pt: HealthPageStrings = {
  hrpBannerDescription:
    "Faça a gestão dos seus dados de saúde e informações médicas",
  hrpConfirmRequiredTitle: "É necessário confirmar os dados",
  hrpConfirmRequiredText:
    "Os seus dados de saúde serão eliminados em breve. Confirme que os seus dados estão corretos.",
  hrpBasicData: "Dados básicos",
  hrpAgeCalculated: "Idade (calculada)",
  hrpNotAvailable: "Não disponível",
  hrpGenderDiverse: "Diverso",
  hrpBodyData: "Dados corporais",
  hrpWeightLastRecorded: "Peso, último registo",
  hrpBmiLastRecorded: "IMC, último registo",
  hrpBodyFatLastRecorded: "Gordura corporal, último registo",
  hrpNoDataRecorded: "Sem dados registados",
  hrpBmiUnderweight: "Baixo peso",
  hrpBmiNormal: "Peso normal",
  hrpBmiOverweight: "Excesso de peso",
  hrpBmiObesity: "Obesidade",
  hrpChartRangeLabel: "Período do gráfico",
  hrpRange7d: "7 dias",
  hrpRange30d: "30 dias",
  hrpRange90d: "90 dias",
  hrpRange1y: "1 ano",
  hrpRangeAll: "Tudo",
  hrpWeightHistory: "Evolução do peso ({count} registos)",
  hrpBodyFatHistory: "Evolução da gordura corporal ({count} registos)",
  hrpNoWeightChartData: "Sem dados de peso para o gráfico",
  hrpFirstEntriesAppear: "Os seus primeiros registos aparecerão aqui",
  hrpCreateTestData: "Criar dados de teste FHIR",
  hrpCreateTestDataHint: "Cria observações FHIR R5 na tabela fHIRResource",
  hrpSectionSaved: "{section} guardado com sucesso.",
  hrpSectionSaveError: "Erro ao guardar: {section}",
  hrpBodyDataSaved: "Dados corporais guardados com sucesso.",
  hrpInputErrorPrefix: "Erro de introdução",
  hrpErrorLoading: "Erro ao carregar os dados de saúde",
  hrpErrorNoBodyValue:
    "Introduza um peso ou uma percentagem de gordura corporal válidos antes de guardar.",
  hrpErrorInvalidBodyInput: "Dados inválidos. Verifique o peso e a altura.",
  hrpErrorSavingBodyData: "Erro ao guardar os dados corporais",
  hrpErrorTestData: "Não foi possível criar os dados de teste FHIR",
  hrpErrorSavingVitals: "Erro ao guardar os sinais vitais",
  hrpErrorSavingLabResults: "Erro ao guardar os resultados laboratoriais",
  hrpErrorLoadingLabResults: "Erro ao carregar os resultados laboratoriais",
  hrpErrorUploadingLabReport: "Erro ao carregar o relatório laboratorial",
  hrpLabResultsSaved: "Resultados laboratoriais guardados com sucesso",
  hrpLabReportUploaded: "Relatório laboratorial carregado com sucesso",
  hrpIcdPlaceholder: "p. ex. I21",
  hrpDisabilities: "Incapacidades",
  hrpPhysicalDisability: "Incapacidade física",
  hrpCognitiveDisability: "Incapacidade cognitiva",
  hrpDisabilityType: "Tipo de incapacidade",
  hrpTravelSupportNeeds: "Necessidades de apoio em viagem",
  hrpSeverityMild: "Ligeira",
  hrpSeverityModerate: "Moderada",
  hrpSeveritySevere: "Grave",
  hrpSecurityTitle: "FHIR R5 e encriptação",
  hrpSecurityText:
    "Todos os dados são transmitidos e armazenados de forma encriptada, em formatos normalizados de dados de saúde. Terceiros só têm acesso com o seu consentimento expresso.",
  hrpDataDeletionLabel: "Eliminação de dados",
  hrpDataDeletionText:
    "Após 12 meses, salvo se confirmar que continuam atuais.",
  hrpManageConsents: "Gerir consentimentos",
  hrpConsentComingSoon:
    "A gestão de consentimentos estará disponível em breve.",

  vitalLoadError: "Erro ao carregar os sinais vitais",
  vitalSaveError: "Erro ao guardar os sinais vitais",

  wellbSaved: "Bem-estar guardado com sucesso",
  wellbSaveFailed: "Não foi possível guardar o bem-estar",

  hrpEpLoadFailed: "Não foi possível carregar os dados de emergência",
  hrpEpLoadFailedTitle: "Não foi possível carregar os dados de emergência",
  hrpEpRetry: "Tentar novamente",
  hrpEpGoToHealthRecords: "Ir para os dados de saúde",
  hrpEpQrGenerated: "Código QR gerado com sucesso",
  hrpEpQrGenerateFailed: "Não foi possível gerar o código QR",
  hrpEpQrRevoked: "Código QR revogado com sucesso",
  hrpEpQrRevokeFailed: "Não foi possível revogar o código QR",
  hrpEpConsentSaved: "Preferências de consentimento guardadas com sucesso",
  hrpEpConsentSaveFailed:
    "Não foi possível guardar as preferências de consentimento",
  hrpEpNoData: "Sem dados de emergência disponíveis",
  hrpEpPdfDownloaded: "PDF do cartão de emergência transferido",
  hrpEpPdfFailed: "Não foi possível gerar o PDF",
  hrpEpToggleUnits: "Alternar sistema de unidades",
  hrpEpImperial: "Imperial",
  hrpEpMetric: "Métrico",
  hrpEpViewQr: "Ver código QR",
  hrpEpActiveUntil: "Ativo até às {time}",
  hrpEpDownloadCard: "Transferir cartão de emergência",
  hrpEpPrintablePdf: "PDF para impressão (inglês/alemão)",
  hrpEpContactsComingSoon:
    "A funcionalidade de contactos de emergência estará disponível em breve.",
  hrpEpPriorityTabs: "Separadores de prioridade",
  hrpEpP1Intro:
    "Estes dados são essenciais para as equipas de emergência. Mantenha-os atualizados.",
  hrpEpP2Intro:
    "Informação médica importante que apoia as decisões de tratamento.",
  hrpEpP3Intro: "Historial clínico e observações adicionais úteis.",
  hrpEpAllergies: "Alergias",
  hrpEpConditions: "Doenças",
  hrpEpEditInHealthRecords: "Editar nos dados de saúde",
  hrpEpNoAllergies: "Sem alergias registadas",
  hrpEpNoMedications: "Sem medicamentos registados",
  hrpEpNoConditions: "Sem doenças registadas",
  hrpEpNoContacts: "Sem contactos de emergência registados",
  hrpEpNotRecordedYet: "Ainda não registado",
  hrpEpNoImmunizations: "Sem vacinas registadas",
  hrpEpNoProcedures: "Sem intervenções registadas",
  hrpEpClinicalObservations: "Observações clínicas",
  hrpEpNoObservations: "Sem observações registadas",
  hrpEpTravelHistory: "Historial de viagens",
  hrpEpNoTravelHistory: "Sem historial de viagens registado",
  hrpEpCriticalityHigh: "risco elevado",
  hrpEpCriticalityLow: "risco baixo",
  hrpEpCriticalityUnknown: "risco não avaliado",
  hrpEpStatusActive: "ativa",
  hrpEpStatusInactive: "inativa",
  hrpEpStatusResolved: "resolvida",
  hrpEpStatusRemission: "em remissão",
  hrpEpStatusRecurrence: "recidiva",
  hrpEpConsentTitle: "Consentimento para partilha de dados de emergência",
  hrpEpConsentIntro:
    "Permita que as equipas de emergência acedam aos seus dados de saúde críticos através de um código QR ou de um token de acesso de emergência.",
  hrpEpShareP1: "Partilhar dados de prioridade 1 (críticos)",
  hrpEpShareP1Items: "Alergias, medicamentos, doenças, contactos de emergência",
  hrpEpShareP2: "Partilhar dados de prioridade 2 (importantes)",
  hrpEpShareP2Items:
    "Grupo sanguíneo, dispositivos médicos, vacinas, altura/peso",
  hrpEpShareP3: "Partilhar dados de prioridade 3 (úteis)",
  hrpEpShareP3Items:
    "Intervenções médicas, observações clínicas, historial de viagens",
  hrpEpConsentAgree: "Compreendo e consinto a partilha dos dados de emergência",
  hrpEpConsentAgreeShort: "Compreendo e consinto",
  hrpEpConsentAgreeText:
    "Compreendo que as equipas de emergência com um token de acesso válido podem consultar os dados de saúde que selecionei. Posso revogar este consentimento a qualquer momento. Os acessos são registados e limitados no tempo.",
  hrpEpSaveConsent: "Guardar consentimento",
  hrpEpKeepUpToDate:
    "Mantenha o seu perfil de emergência atualizado. Estes dados são partilhados com as equipas de emergência quando gera um código QR.",
  hrpEpProfileValidation: "Validação do perfil",
  hrpEpAccessAuditLog: "Registo de acessos",
  hrpEpConsentActive: "Consentimento de emergência ativo",
  hrpEpConsentGivenOn: "Consentimento dado em {date}",
  hrpEpDemographics: "Dados pessoais",
  hrpEpDemographicsDetail: "Dados pessoais (grupo sanguíneo, altura, peso)",
  hrpEpMedicalDevices: "Dispositivos médicos",
  hrpEpConsentDialogIntro:
    "Selecione os dados que pretende partilhar com as equipas de emergência. O seu consentimento fica guardado para futuros códigos QR.",
  hrpEpP1Heading: "Prioridade 1: dados críticos",
  hrpEpP2Heading: "Prioridade 2: dados importantes",
  hrpEpConsentNote:
    "Recomendamos partilhar todos os dados críticos para garantir cuidados de emergência adequados. Pode revogar o acesso a qualquer momento.",
  hrpEpConfirmGenerate: "Confirmar e gerar código QR",
  hrpEpExpires: "Expira",
  hrpEpExpiryNote:
    "Por segurança, este código QR expira automaticamente após o período definido.",
  hrpEpLinkCopied: "Ligação copiada para a área de transferência",
  hrpEpRevokeAccess: "Revogar acesso",
};

export const healthPageStrings: Record<Language, HealthPageStrings> = {
  en,
  de,
  fr,
  es,
  pt,
};
